import React, { useCallback, useState } from 'react'

export interface ToggleButtonGroupItem {
    key: string
    label?: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    iconOnly?: boolean
    disabled?: boolean
    /** Native tooltip; also the only readable name an `iconOnly` item has. */
    title?: string
}

export interface ToggleButtonGroupProps {
    items: ToggleButtonGroupItem[]
    size?: 'sm' | 'md' | 'lg'
    isAttached?: boolean
    orientation?: 'horizontal' | 'vertical'
    width?: 'hug' | 'fill'
    showDivider?: boolean
    selectionMode?: 'single' | 'multiple'
    selectedKeys?: Set<string>
    defaultSelectedKeys?: Set<string>
    onSelectionChange?: (keys: Set<string>) => void
    style?: React.CSSProperties
}

const sizeConfig = {
    sm: { height: 'h-7', minHeight: 'min-h-7', padding: 'px-2.5 py-1', iconPadding: 'px-1.5 py-1', font: 'text-xs leading-[1.34]' },
    md: { height: 'h-9', minHeight: 'min-h-9', padding: 'px-4 py-2', iconPadding: 'px-2.5 py-2', font: 'text-sm leading-[1.43]' },
    lg: { height: 'h-11', minHeight: 'min-h-11', padding: 'px-5 py-2.5', iconPadding: 'px-3 py-2.5', font: 'text-base leading-[1.5]' },
}

export const ToggleButtonGroup = ({
    items,
    size = 'md',
    isAttached = true,
    orientation = 'horizontal',
    width = 'hug',
    showDivider = true,
    selectionMode = 'single',
    selectedKeys: controlledKeys,
    defaultSelectedKeys = new Set<string>(),
    onSelectionChange,
    style,
}: ToggleButtonGroupProps) => {
    const [internalKeys, setInternalKeys] = useState<Set<string>>(defaultSelectedKeys)
    const selected = controlledKeys ?? internalKeys
    const config = sizeConfig[size]
    const isVertical = orientation === 'vertical'
    const isFill = width === 'fill'

    const handleSelect = useCallback(
        (key: string) => {
            const next = new Set(selected)
            if (selectionMode === 'single') {
                if (next.has(key)) next.clear()
                else {
                    next.clear()
                    next.add(key)
                }
            } else {
                if (next.has(key)) next.delete(key)
                else next.add(key)
            }
            if (!controlledKeys) setInternalKeys(next)
            onSelectionChange?.(next)
        },
        [selected, selectionMode, controlledKeys, onSelectionChange]
    )

    return (
        <div
            role="group"
            className={`inline-flex ${isVertical ? 'flex-col' : 'flex-row'}
                ${isAttached ? 'gap-0 overflow-hidden rounded-3xl bg-default' : 'gap-1'}
                ${isFill ? 'w-full' : ''}`}
            style={style}
        >
            {items.map((item, i) => {
                const isSelected = selected.has(item.key)

                const stateClasses = isSelected
                    ? 'bg-accent text-accent-foreground enabled:hover:bg-accent-hover'
                    : isAttached
                      ? 'bg-transparent text-foreground enabled:hover:bg-default-hover'
                      : 'bg-default text-foreground enabled:hover:bg-default-hover'

                return (
                    <React.Fragment key={item.key}>
                        {isAttached && showDivider && i > 0 && !isSelected && !selected.has(items[i - 1].key) && (
                            <div className={`shrink-0 self-center bg-border ${isVertical ? 'h-px w-[18px]' : 'h-[18px] w-px'}`} />
                        )}
                        <button
                            type="button"
                            disabled={item.disabled}
                            aria-pressed={isSelected}
                            title={item.title}
                            onClick={() => !item.disabled && handleSelect(item.key)}
                            className={`box-border inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-3xl border-none font-medium outline-none transition-colors duration-150
                                disabled:cursor-not-allowed disabled:opacity-50
                                ${isVertical ? '' : config.height} ${config.minHeight} ${item.iconOnly ? config.iconPadding : config.padding} ${config.font}
                                ${stateClasses} ${isFill ? 'flex-1' : ''}`}
                        >
                            {item.prefix && <span className="flex shrink-0 items-center">{item.prefix}</span>}
                            {!item.iconOnly && item.label && <span>{item.label}</span>}
                            {item.suffix && <span className="flex shrink-0 items-center">{item.suffix}</span>}
                        </button>
                    </React.Fragment>
                )
            })}
        </div>
    )
}
